import { fmtBRLCompacto, fmtPct } from './painelFormat';
import type { ClasseDesvio, SopCobertura, SopLinha, SopMes, SopPayload, SopResumoMes } from '@/types/dashboards';

/**
 * Seletores puros do painel S&OP da Equiplex.
 *
 * Regra de negócio (desvio, faixa, cobertura, calendário 16→15) vem pronta do
 * backend. Aqui só se recorta pelo filtro da tela e se soma — e a soma de
 * desvio usa o mesmo denominador do backend (`programado_base`), para o KPI de
 * três meses bater com a tabela mês a mês.
 *
 * Mês em andamento: o desvio só olha os dias fechados (até `fechado_ate`, corte
 * − 2 dias, porque a OP é reportada no fim do processo, em D+1/D+2). Programado e
 * produzido saem das mesmas OPs: `programado_base` × `produzido_base`.
 */

/** Produzido na mesma régua do `programado_base` (snapshot antigo não tem `produzido_base`). */
export const produzidoBase = (l: { produzido: number; produzido_base?: number }): number => l.produzido_base ?? l.produzido;

/** Último dia que entra na comparação do mês aberto (snapshot antigo: o próprio corte). */
export const fechadoAte = (p: { corte: string; fechado_ate?: string }): string => p.fechado_ate ?? p.corte;

export interface ResumoAgregado {
  programado: number;
  programadoBase: number;
  /** Total das OPs do recorte (o mês aberto inteiro, inclusive os dias ainda abertos). */
  produzido: number;
  /** Na mesma régua do `programadoBase`: é este que entra no desvio. */
  produzidoBase: number;
  desvioPct: number | null;
  aderenciaPct: number | null;
  faturadoValor: number;
  faturadoQtd: number;
  pmv: number | null;
  criticos: number;
  alertas: number;
  lotesPendentesCq: number;
  mesesComDesvio: number;
}

const arred1 = (v: number): number => Math.round(v * 10) / 10;

export function agregarResumo(resumos: SopResumoMes[], meses: string[]): ResumoAgregado {
  const sel = resumos.filter((r) => meses.includes(r.mes));
  const soma = (f: (r: SopResumoMes) => number) => sel.reduce((acc, r) => acc + f(r), 0);
  const programadoBase = soma((r) => r.programado_base);
  const produzido = soma((r) => r.produzido);
  const prodBase = soma(produzidoBase);
  const faturadoValor = soma((r) => r.faturado_valor);
  const faturadoQtd = soma((r) => r.faturado_qtd);
  const temBase = programadoBase > 0;
  return {
    programado: soma((r) => r.programado),
    programadoBase,
    produzido,
    produzidoBase: prodBase,
    desvioPct: temBase ? arred1(((prodBase - programadoBase) / programadoBase) * 100) : null,
    aderenciaPct: temBase ? arred1((prodBase / programadoBase) * 100) : null,
    faturadoValor,
    faturadoQtd,
    pmv: faturadoQtd > 0 ? faturadoValor / faturadoQtd : null,
    criticos: soma((r) => r.criticos),
    alertas: soma((r) => r.alertas),
    lotesPendentesCq: soma((r) => r.lotes_pendentes_cq),
    mesesComDesvio: sel.filter((r) => r.desvio_pct !== null).length,
  };
}

export type FaixaDistribuicao = Exclude<ClasseDesvio, 'futuro'>;

/** Quantos SKU×mês caem em cada faixa. Mês futuro não tem desvio e fica fora. */
export function distribuicaoFaixas(linhas: SopLinha[], meses: string[]): Record<FaixaDistribuicao, number> {
  const out: Record<FaixaDistribuicao, number> = { critico: 0, alerta: 0, normal: 0, sem_programa: 0 };
  for (const l of linhas) {
    if (!meses.includes(l.mes) || l.classe === 'futuro') continue;
    out[l.classe] += 1;
  }
  return out;
}

export type ModoTopDesvio = 'unidades' | 'percentual';

export interface TopDesvio {
  chave: string;
  codigo: string;
  codigoDatasul: string;
  mes: string;
  gap: number;
  desvioPct: number;
  programadoBase: number;
  produzido: number;
}

/**
 * Os maiores desvios do recorte. Em unidades por padrão: em percentual, uma OP
 * pequena que não rodou (−100%) empurra para fora do ranking o SKU que deixou
 * de produzir milhões — o percentual fica como alternativa, não como padrão.
 */
export function topDesvios(linhas: SopLinha[], meses: string[], modo: ModoTopDesvio, limite = 10): TopDesvio[] {
  return linhas
    .filter((l) => meses.includes(l.mes) && l.desvio_pct !== null && l.programado_base > 0)
    .map((l) => ({
      chave: `${l.codigo}-${l.mes}`,
      codigo: l.codigo,
      codigoDatasul: l.codigo_datasul,
      mes: l.mes,
      gap: produzidoBase(l) - l.programado_base,
      desvioPct: l.desvio_pct as number,
      programadoBase: l.programado_base,
      produzido: produzidoBase(l),
    }))
    .sort((a, b) =>
      modo === 'unidades' ? Math.abs(b.gap) - Math.abs(a.gap) : Math.abs(b.desvioPct) - Math.abs(a.desvioPct),
    )
    .slice(0, limite);
}

export type FiltroFaixa = 'todas' | ClasseDesvio;

export interface FiltroLinhas {
  meses: string[];
  busca: string;
  faixa: FiltroFaixa;
}

const ORDEM_CLASSE: Record<ClasseDesvio, number> = { critico: 0, alerta: 1, normal: 2, futuro: 3, sem_programa: 4 };

/** Linhas da tabela: busca por código (com ou sem ponto) ou descrição; piores primeiro. */
export function filtrarLinhas(payload: SopPayload, filtro: FiltroLinhas): SopLinha[] {
  const termo = filtro.busca.trim().toLowerCase();
  const termoSemPonto = termo.replace(/\./g, '');
  return payload.linhas
    .filter((l) => filtro.meses.includes(l.mes))
    .filter((l) => filtro.faixa === 'todas' || l.classe === filtro.faixa)
    .filter((l) => {
      if (!termo) return true;
      const desc = payload.itens[l.codigo_datasul]?.descricao.toLowerCase() ?? '';
      return (termoSemPonto && l.codigo.includes(termoSemPonto)) || desc.includes(termo);
    })
    .sort(
      (a, b) =>
        ORDEM_CLASSE[a.classe] - ORDEM_CLASSE[b.classe] ||
        Math.abs(produzidoBase(b) - b.programado_base) - Math.abs(produzidoBase(a) - a.programado_base) ||
        a.mes.localeCompare(b.mes) ||
        a.codigo.localeCompare(b.codigo),
    );
}

const addDia = (iso: string, n: number): string => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

export type PontoDiario = { data: string } & Record<string, number | string>;

/**
 * Série diária por categoria de volume. Dia sem produção reportada vale zero (não
 * houve produção apontada), mas só até o corte da réplica: depois dele o dado
 * ainda não existe, e zero ali seria mentira.
 */
const dentroDeAlgum = (dia: string, meses: SopMes[]): boolean => meses.some((m) => dia >= m.start && dia <= m.end);

function somarPorDia(payload: SopPayload, meses: SopMes[], categorias: string[]): Map<string, Record<string, number>> {
  const porDia = new Map<string, Record<string, number>>();
  for (const d of payload.diario) {
    if (d.data > payload.corte || !categorias.includes(d.categoria) || !dentroDeAlgum(d.data, meses)) continue;
    const dia = porDia.get(d.data) ?? {};
    dia[d.categoria] = (dia[d.categoria] ?? 0) + d.produzido;
    porDia.set(d.data, dia);
  }
  return porDia;
}

export function serieDiaria(payload: SopPayload, meses: string[], categorias: string[]): PontoDiario[] {
  const sel = payload.meses.filter((m) => meses.includes(m.key) && !m.futuro);
  if (sel.length === 0 || categorias.length === 0) return [];
  const inicio = sel.map((m) => m.start).sort()[0];
  const fimMeses = sel.map((m) => m.end).sort()[sel.length - 1];
  const fim = fimMeses < payload.corte ? fimMeses : payload.corte;
  const porDia = somarPorDia(payload, sel, categorias);
  const pontos: PontoDiario[] = [];
  for (let dia = inicio; dia <= fim; dia = addDia(dia, 1)) {
    // Seleção não contígua (ex.: maio e setembro): pula os dias dos meses de fora.
    if (!dentroDeAlgum(dia, sel)) continue;
    const valores = porDia.get(dia) ?? {};
    pontos.push(Object.fromEntries([['data', dia], ...categorias.map((c) => [c, valores[c] ?? 0])]) as PontoDiario);
  }
  return pontos;
}

/** Categorias com mais volume no recorte — o padrão do gráfico quando 100/250 ML não existem. */
export function categoriasPadrao(payload: SopPayload, meses: string[]): string[] {
  const preferidas = ['100 ML', '250 ML'].filter((c) => payload.categorias_volume.includes(c));
  if (preferidas.length > 0) return preferidas;
  const sel = payload.meses.filter((m) => meses.includes(m.key));
  const total = new Map<string, number>();
  for (const d of payload.diario) {
    if (!sel.some((m) => d.data >= m.start && d.data <= m.end)) continue;
    total.set(d.categoria, (total.get(d.categoria) ?? 0) + d.produzido);
  }
  return [...total.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([c]) => c);
}

/** Meses do recorte com faturamento abaixo do esperado na réplica. */
export function coberturaBaixa(payload: SopPayload, meses: string[]): SopCobertura[] {
  return payload.cobertura_faturamento.filter((c) => meses.includes(c.mes) && c.status === 'baixo');
}

export function mesPorChave(payload: SopPayload): Record<string, SopMes> {
  return Object.fromEntries(payload.meses.map((m) => [m.key, m]));
}

/** `0.37` da mediana → `37,0% do esperado (R$ 21,4 mi)`. */
export function descreverCobertura(c: SopCobertura): string {
  if (c.status === 'sem_dados') return 'a réplica ainda não chegou neste mês';
  if (c.razao === null || c.referencia_mediana === null) return 'sem histórico para comparar';
  const esperado = c.referencia_mediana * c.fracao_periodo;
  return `${fmtPct(c.razao * 100)} do esperado (${fmtBRLCompacto(esperado)})`;
}
